import { useState, useEffect } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'http://config.not.loaded.xxx';

// HTTP utility with timeout and retry logic for browser environments
class HttpClient {
  private static async delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private static async fetchWithTimeout(
    url: string,
    options: RequestInit = {},
    timeout: number = 10000
  ): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  public static async fetchWithRetry(
    url: string,
    options: RequestInit = {},
    config: {
      timeout?: number;
      maxRetries?: number;
      retryDelay?: number;
    } = {}
  ): Promise<Response> {
    const { timeout = 10000, maxRetries = 5, retryDelay = 5000 } = config;

    let lastError: Error | undefined;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await this.fetchWithTimeout(url, options, timeout);
        return response;
      } catch (error) {
        lastError = error as Error;

        if (attempt === maxRetries) {
          // Only log timeout on the last attempt
          console.error(
            `HTTP request failed after ${maxRetries} attempts:`,
            error
          );
          throw new Error(lastError?.message || 'Request failed after retries');
        }

        // Wait before retrying
        await this.delay(retryDelay);
      }
    }

    // This should never be reached, but TypeScript requires it
    throw new Error('Request failed unexpectedly');
  }

  public static async get(
    url: string,
    config?: Parameters<typeof HttpClient.fetchWithRetry>[2]
  ): Promise<Response> {
    return this.fetchWithRetry(url, { method: 'GET' }, config);
  }

  public static async post(
    url: string,
    body?: unknown,
    config?: Parameters<typeof HttpClient.fetchWithRetry>[2]
  ): Promise<Response> {
    return this.fetchWithRetry(
      url,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      },
      config
    );
  }
}

export interface Hotel {
  id: string;
  name: string;
  description: string;
  city: string;
  country: string;
  rating: number;
  images: string[];
  priceRange: {
    min: number;
    max: number;
  };
  amenities: string[];
}

// Custom hook for fetching hotels
export const useHotels = () => {
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchHotels = async () => {
      try {
        setLoading(true);
        const response = await HttpClient.get(`${API_URL}/api/hotels`);

        if (!response.ok) {
          throw new Error('Failed to fetch hotels');
        }

        const data = await response.json();
        setHotels(data.data || []);
        setError(null);
      } catch {
        // Using fallback mock data for hotels
        setHotels(getMockHotels());
        setError(null);
      } finally {
        setLoading(false);
      }
    };

    fetchHotels();
  }, []);

  return { hotels, loading, error };
};

// Contact form submission
export const useContactForm = () => {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitContact = async (formData: {
    name: string;
    email: string;
    message: string;
  }) => {
    try {
      setSubmitting(true);
      setError(null);

      const response = await HttpClient.post(
        `${API_URL}/api/contact`,
        formData
      );

      if (!response.ok) {
        throw new Error('Failed to submit contact form');
      }

      setSuccess(true);
    } catch (err) {
      setError('Failed to submit form. Please try again.');
      console.error('Contact form error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return { submitContact, submitting, success, error };
};

// Mock hotels data fallback
const getMockHotels = (): Hotel[] => [
  {
    id: '1',
    name: 'Grand Hotel Plaza',
    description:
      'Luxury hotel in the heart of the city with world-class amenities',
    city: 'New York',
    country: 'USA',
    rating: 4.5,
    images: ['hotel1.jpg'],
    priceRange: { min: 200, max: 500 },
    amenities: ['WiFi', 'Pool', 'Gym', 'Spa', 'Restaurant'],
  },
  {
    id: '2',
    name: 'Seaside Resort',
    description: 'Beautiful beachfront resort with stunning ocean views',
    city: 'Miami',
    country: 'USA',
    rating: 4.2,
    images: ['resort1.jpg'],
    priceRange: { min: 150, max: 400 },
    amenities: ['WiFi', 'Pool', 'Beach', 'Restaurant', 'Bar'],
  },
  {
    id: '3',
    name: 'Mountain Lodge',
    description: 'Cozy lodge with breathtaking mountain views and ski access',
    city: 'Denver',
    country: 'USA',
    rating: 4.0,
    images: ['lodge1.jpg'],
    priceRange: { min: 100, max: 300 },
    amenities: ['WiFi', 'Fireplace', 'Ski', 'Restaurant'],
  },
];
